
import React, { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/base/SEOHead';

const CareersPage: React.FC = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    position: '',
    experience: '',
    education: '',
    certifications: '',
    availability: '',
    salary: '',
    coverLetter: '',
    resume: null as File | null
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setFormData(prev => ({
      ...prev,
      resume: file
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const submitData = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (key === 'resume' && value instanceof File) {
          submitData.append(key, 'Uncollectable');
        } else if (value) {
          submitData.append(key, value.toString());
        }
      });

      const response = await fetch('https://readdy.ai/api/form/d3ui0oqb8acecn2ov5bg', {
        method: 'POST',
        body: submitData
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          position: '',
          experience: '',
          education: '',
          certifications: '',
          availability: '',
          salary: '',
          coverLetter: '',
          resume: null
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead 
        title="Careers at HP Inc Consulting Services - Join Our Safety Consulting Team"
        description="Join HP Inc Consulting Services team of safety professionals. Explore career opportunities in safety consulting, OSHA compliance, industrial hygiene, and safety training across the Northeast."
        keywords="safety consulting careers, OSHA consultant jobs, industrial hygienist jobs, safety training careers, safety consultant employment, workplace safety careers, construction safety jobs, New York safety jobs"
        canonical="https://hpincconsultant.com/careers"
        ogTitle="Careers at HP Inc Consulting Services - Safety Professional Opportunities"
        ogDescription="Join our team of expert safety professionals. Explore rewarding career opportunities in safety consulting and workplace safety."
        ogUrl="https://hpincconsultant.com/careers"
      />
      <Header />
      <main>
        {/* Hero Section */}
        <section className="relative bg-gradient-to-r from-blue-900 to-blue-700 text-white py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Careers
              </h1>
              <p className="text-xl mb-8">
                Join our team of safety professionals
              </p>
            </div>
          </div>
        </section>

        {/* Careers Section */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">Why Work With Us?</h2>
                <p className="text-gray-600 text-lg">Join a team dedicated to making workplaces safer</p>
              </div>
              
              <div className="grid md:grid-cols-3 gap-8 mb-12">
                <div className="text-center">
                  <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-team-line text-2xl text-white"></i>
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Great Team</h3>
                  <p className="text-gray-600">Work with experienced professionals who share your passion for safety.</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-graduation-cap-line text-2xl text-white"></i>
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Growth Opportunities</h3>
                  <p className="text-gray-600">Continuous learning and professional development opportunities.</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-heart-line text-2xl text-white"></i>
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Meaningful Work</h3>
                  <p className="text-gray-600">Make a real difference in protecting workers and saving lives.</p>
                </div>
              </div>

              <div className="bg-gray-50 p-8 rounded-lg mb-12">
                <h3 className="text-2xl font-bold mb-6">Current Openings</h3>
                <div className="space-y-4">
                  <div className="border-b border-gray-200 pb-4">
                    <h4 className="text-lg font-semibold">Safety Consultant</h4>
                    <p className="text-gray-600">Full-time position for experienced safety professional</p>
                  </div>
                  <div className="border-b border-gray-200 pb-4">
                    <h4 className="text-lg font-semibold">Industrial Hygienist</h4>
                    <p className="text-gray-600">Certified industrial hygienist for workplace assessments</p>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold">Training Coordinator</h4>
                    <p className="text-gray-600">Coordinate and deliver OSHA training programs</p>
                  </div>
                </div>
              </div>

              {/* Job Application Form */}
              <div className="bg-white border border-gray-200 rounded-lg p-8">
                <h3 className="text-2xl font-bold mb-6 text-center">Apply for a Position</h3>
                <p className="text-gray-600 text-center mb-8">
                  Fill out the form below to submit your application. We'll review your information and get back to you soon.
                </p>

                <form onSubmit={handleSubmit} data-readdy-form id="job-application-form" className="space-y-6">
                  {/* Personal Information */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                        First Name *
                      </label>
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        placeholder="Enter your first name"
                      />
                    </div>
                    <div>
                      <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        placeholder="Enter your last name"
                      />
                    </div>
                  </div>

                  {/* Contact Information */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        placeholder="your.email@example.com"
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        placeholder="(555) 123-4567"
                      />
                    </div>
                  </div>

                  {/* Position and Experience */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="position" className="block text-sm font-medium text-gray-700 mb-2">
                        Position of Interest *
                      </label>
                      <div className="relative">
                        <select
                          id="position"
                          name="position"
                          value={formData.position}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm appearance-none pr-8"
                        >
                          <option value="">Select a position</option>
                          <option value="Safety Consultant">Safety Consultant</option>
                          <option value="Industrial Hygienist">Industrial Hygienist</option>
                          <option value="Training Coordinator">Training Coordinator</option>
                          <option value="Safety Manager">Safety Manager</option>
                          <option value="OSHA Trainer">OSHA Trainer</option>
                          <option value="Other">Other</option>
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                          <i className="ri-arrow-down-s-line text-gray-400"></i>
                        </div>
                      </div>
                    </div>
                    <div>
                      <label htmlFor="experience" className="block text-sm font-medium text-gray-700 mb-2">
                        Years of Experience *
                      </label>
                      <div className="relative">
                        <select
                          id="experience"
                          name="experience"
                          value={formData.experience}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm appearance-none pr-8"
                        >
                          <option value="">Select experience level</option>
                          <option value="0-1 years">0-1 years</option>
                          <option value="2-5 years">2-5 years</option>
                          <option value="6-10 years">6-10 years</option>
                          <option value="11-15 years">11-15 years</option>
                          <option value="15+ years">15+ years</option>
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                          <i className="ri-arrow-down-s-line text-gray-400"></i>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Education and Certifications */}
                  <div>
                    <label htmlFor="education" className="block text-sm font-medium text-gray-700 mb-2">
                      Education Background *
                    </label>
                    <input
                      type="text"
                      id="education"
                      name="education"
                      value={formData.education}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="e.g., Bachelor's in Occupational Safety, Master's in Industrial Hygiene"
                    />
                  </div>

                  <div>
                    <label htmlFor="certifications" className="block text-sm font-medium text-gray-700 mb-2">
                      Relevant Certifications
                    </label>
                    <input
                      type="text"
                      id="certifications"
                      name="certifications"
                      value={formData.certifications}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="e.g., CSP, CIH, CHST, OSHA 30-Hour, etc."
                    />
                  </div>

                  {/* Availability and Salary */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="availability" className="block text-sm font-medium text-gray-700 mb-2">
                        Availability *
                      </label>
                      <div className="relative">
                        <select
                          id="availability"
                          name="availability"
                          value={formData.availability}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm appearance-none pr-8"
                        >
                          <option value="">Select availability</option>
                          <option value="Immediately">Immediately</option>
                          <option value="2 weeks notice">2 weeks notice</option>
                          <option value="1 month">1 month</option>
                          <option value="2-3 months">2-3 months</option>
                          <option value="Other">Other</option>
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                          <i className="ri-arrow-down-s-line text-gray-400"></i>
                        </div>
                      </div>
                    </div>
                    <div>
                      <label htmlFor="salary" className="block text-sm font-medium text-gray-700 mb-2">
                        Salary Expectations
                      </label>
                      <input
                        type="text"
                        id="salary"
                        name="salary"
                        value={formData.salary}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        placeholder="e.g., $60,000 - $80,000"
                      />
                    </div>
                  </div>

                  {/* Resume Upload */}
                  <div>
                    <label htmlFor="resume" className="block text-sm font-medium text-gray-700 mb-2">
                      Resume/CV *
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                      <input
                        type="file"
                        id="resume"
                        name="resume"
                        onChange={handleFileChange}
                        accept=".pdf,.doc,.docx"
                        required
                        className="hidden"
                      />
                      <label htmlFor="resume" className="cursor-pointer">
                        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                          <i className="ri-upload-cloud-line text-2xl text-blue-600"></i>
                        </div>
                        <p className="text-sm text-gray-600 mb-1">
                          {formData.resume ? formData.resume.name : 'Click to upload your resume'}
                        </p>
                        <p className="text-xs text-gray-500">PDF, DOC, or DOCX (max 10MB)</p>
                      </label>
                    </div>
                  </div>

                  {/* Cover Letter */}
                  <div>
                    <label htmlFor="coverLetter" className="block text-sm font-medium text-gray-700 mb-2">
                      Cover Letter / Why do you want to work with us? *
                    </label>
                    <textarea
                      id="coverLetter"
                      name="coverLetter"
                      value={formData.coverLetter}
                      onChange={handleInputChange}
                      required
                      maxLength={500}
                      rows={6}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
                      placeholder="Tell us about your passion for workplace safety and why you'd like to join our team..."
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {formData.coverLetter.length}/500 characters
                    </p>
                  </div>

                  {/* Submit Button */}
                  <div className="text-center pt-6">
                    <button
                      type="submit"
                      disabled={isSubmitting || formData.coverLetter.length > 500}
                      className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                    >
                      {isSubmitting ? (
                        <>
                          <i className="ri-loader-4-line animate-spin mr-2"></i>
                          Submitting Application...
                        </>
                      ) : (
                        <>
                          <i className="ri-send-plane-line mr-2"></i>
                          Submit Application
                        </>
                      )}
                    </button>
                  </div>

                  {/* Status Messages */}
                  {submitStatus === 'success' && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                      <div className="flex items-center justify-center mb-2">
                        <i className="ri-check-circle-line text-2xl text-green-600 mr-2"></i>
                        <h4 className="text-lg font-semibold text-green-800">Application Submitted!</h4>
                      </div>
                      <p className="text-green-700">
                        Thank you for your interest! We'll review your application and get back to you within 5-7 business days.
                      </p>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                      <div className="flex items-center justify-center mb-2">
                        <i className="ri-error-warning-line text-2xl text-red-600 mr-2"></i>
                        <h4 className="text-lg font-semibold text-red-800">Submission Failed</h4>
                      </div>
                      <p className="text-red-700">
                        There was an error submitting your application. Please try again or contact us directly.
                      </p>
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default CareersPage;
